import { FC } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useHasPermission } from '@/entities/profile';
import { UserGroupListItem, useUserGroups } from '@/entities/userGroups';
import { NestedItems } from '@/features/nested-items';
import { useServerSearch } from '@/shared/hooks';
import { UserFormValues } from '../UserForm/types';

/** Сколько групп показывать в окне: список набирается поиском по всему набору, а не листается */
const GROUPS_PAGE_LIMIT = 50;

const getGroupKey = (group: UserGroupListItem) => String(group.id);
const getGroupName = (group: UserGroupListItem) => group.name;

type Props = {
  disabled?: boolean;
};

/**
 * Группы пользователя: строки состава из данных карточки, окно выбора открывается с присвоенными
 * группами отмеченными и заменяет ими состав. Группа ищется по всему набору на сервере; группа вне
 * выдачи окна остаётся в составе отмеченной — её видно в карточке и можно снять в окне.
 */
export const UserGroupsList: FC<Props> = ({ disabled }) => {
  const { t } = useTranslation(['users', 'common']);
  const { setValue, watch } = useFormContext<UserFormValues>();
  const currentGroups = watch('userGroups');

  // Справочник групп нужен только окну выбора: состав строится из данных карточки, исключению группы
  // справочник не нужен. Он закрыт своим правом — без него окно не предлагается и запроса нет
  const canViewGroups = useHasPermission('VIEW_USER_GROUP');

  // Поиск идёт по всему набору групп: группа за пределами первой порции иначе недостижима
  const search = useServerSearch();
  const { data, isLoading } = useUserGroups(
    { search: search.applied || undefined, limit: GROUPS_PAGE_LIMIT },
    { enabled: canViewGroups && !disabled },
  );
  const allGroups = data?.userGroups ?? [];

  const handleChange = (groups: UserGroupListItem[]) => {
    setValue(
      'userGroups',
      groups.map((group) => ({ id: group.id, name: group.name })),
    );
  };

  return (
    <NestedItems<UserGroupListItem>
      labels={{
        title: t('group'),
        addButton: t('users:addGroup'),
        removeButton: t('users:removeGroupFromUser'),
        emptyState: t('noGroupsInUser'),
        dialogTitle: t('users:addGroup'),
        noItemsFound: t('users:notFound'),
      }}
      value={currentGroups}
      onChange={handleChange}
      allItems={allGroups}
      total={data?.total}
      isLoading={isLoading}
      getItemId={getGroupKey}
      renderTitle={getGroupName}
      serverSearch={search}
      disabled={disabled}
      canAdd={canViewGroups}
    />
  );
};
